import { useState } from "react";

import { useForm } from "react-hook-form";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, FormControl } from "@mui/material";
import { yupResolver } from '@hookform/resolvers/yup';

import { ErrorAlert, SuccessAlert } from "@shared/ui/alerts";
import { InputNumberFormatController, InputController } from "@shared/ui/form";
import UploadFile from "@vendor/examples/UploadFile";
import { resendManualInvoice } from "../api/invoiceApi";
import { buildResendInvoiceManualSchema } from "../schema/resendInvoiceManualSchema";
import { isAllowedInvoiceFileType, isWithinInvoiceFileSize } from "../lib/invoiceFileRules";
import { buildResendInvoiceFormData } from "../lib/invoicePayloads";

import type { ChangeEvent } from "react";
import type { TFunction } from "i18next";
import type { InvoiceFormValues } from "../lib/invoicePayloads";

export interface ResendInvoiceManualProps {
    handleClose: () => void;
    open: boolean;
    notLoading: () => void;
    loading: () => void;
    title: string;
    invoiceToResend: number | null;
    reloadInvoice: () => void;
    t: TFunction;
}

interface ValidateFileState {
    required: boolean;
    type: boolean;
    size: boolean;
}

const validationFileInitial: ValidateFileState = {
    required: false,
    type: false,
    size: false
}



export const ResendInvoiceManual = ({handleClose, open, notLoading, loading, title, invoiceToResend, reloadInvoice, t}: ResendInvoiceManualProps) => {
    const schema = buildResendInvoiceManualSchema(t);

    const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<InvoiceFormValues>({
        resolver: yupResolver(schema) as never,
    });

    const [file, setFile] = useState<File | undefined>()
    const [validateFile, setValidateFile] = useState<ValidateFileState>(validationFileInitial)

    const resetField = () => {
        reset({
            ccEmail: "",
            total: "",
            comments: "",
            itbmsTax: 0,
            subtotal: 0,
        })
        setFile(undefined)
    }

    const validateSizeFile = (files: File) => {
        const tooLarge = !isWithinInvoiceFileSize(files)
        setValidateFile({ required: false, type: false, size: tooLarge })
        return tooLarge
    }

    const validateTypeFile = (files: File) => {
        const allowed = isAllowedInvoiceFileType(files)
        setValidateFile({ required: false, type: !allowed, size: false })
        return allowed
    }

    const requiredFile = () => {
        setValidateFile({
            type: false,
            size: false,
            required: true,
        })
    }

    const handleChangeFile = (e: ChangeEvent<HTMLInputElement>) => {
        // Comportamiento preexistente (ver mismo patrón en usePermission.ts): `files` puede ser
        // `null` en el tipo del DOM, pero el runtime siempre lo llena en este flujo de carga.
        if (e.target.files!.length) {
            // `length` distinto de cero garantiza que el primer elemento existe.
            const selectedFile = e.target.files![0]!
            let isFileType = validateTypeFile(selectedFile)
            if (isFileType) {
                let isFileSize = validateSizeFile(selectedFile)
                if (!isFileSize) {
                    setFile(selectedFile)
                }
            }
            return null
        }
        requiredFile()
    };

    const onClose = () => {
        setFile(undefined)
        setValidateFile(validationFileInitial)
        resetField()
        handleClose()
    }

    const onRegister = (value: InvoiceFormValues) => {
        if (file === undefined) {
            requiredFile()
            return null;
        }

        if (!validateTypeFile(file) && validateSizeFile(file)) {
            return null;
        }

        loading()
        onClose()
        // `invoiceToResend` viene de `InvoicePage` (`useState<number | null>`), solo se setea antes
        // de abrir este diálogo: en el submit siempre está definido (comportamiento preexistente,
        // sin guard nuevo).
        const formData = buildResendInvoiceFormData(value, file, invoiceToResend as number)

        resendManualInvoice(formData).then(() => {
            notLoading()
            SuccessAlert(t("invoiceResentTitle"), t("invoiceResentMessage")).then(() => reloadInvoice())
        }).catch((error) => {
            const data = error.response?.data;
            notLoading()
            ErrorAlert(t("errorUploadInvoice"), data?.message ?? t("internalError")).then()
        })
    }

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <form onSubmit={handleSubmit(onRegister)}>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogContent>
                        <Box sx={{p: 1}}>
                            <Grid container spacing={3}>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        {/* `type` no es una prop de `InputNumberFormatController` (nunca se leía en
                                            runtime); se quita acá porque JSX ahora la rechaza, sin cambio de comportamiento. */}
                                        <InputNumberFormatController name="subtotal" control={control}
                                            defaultValue="" label={t("subTotal")} />
                                        {errors.subtotal &&
                                            <span style={{ fontSize: 12, color: "red" }}>{errors.subtotal.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        <InputNumberFormatController name="itbmsTax" control={control}
                                            defaultValue="" label="ITBMS" />
                                        {errors.itbmsTax &&
                                            <span style={{ fontSize: 12, color: "red" }}>{errors.itbmsTax.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        <InputNumberFormatController name="total" control={control}
                                            defaultValue="" label={t("total")} />
                                        {errors.total &&
                                            <span style={{ fontSize: 12, color: "red" }}>{errors.total.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        <InputController name="ccEmail" control={control}
                                            type="email"
                                            defaultValue="" label={t("ccEmail")} />
                                        {errors.ccEmail &&
                                            <span style={{ fontSize: 12, color: "red" }}>{errors.ccEmail.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <FormControl fullWidth>
                                        <InputController name="comments" control={control}
                                            type="string"
                                            defaultValue="" label={t("comments")} />
                                        {errors.comments &&
                                            <span
                                                style={{ fontSize: 12, color: "red" }}>{errors.comments.message}</span>}
                                    </FormControl>
                                </Grid>
                                <Grid size={12}>
                                    <Box sx={{mb: 2}}>
                                        <UploadFile
                                            id="coverPage"
                                            nameInput="cover"
                                            onChangeFile={handleChangeFile}
                                            file={file}
                                            textButton={t("loadInvoice")}
                                            fileLoaded={t("loadedInvoice")}
                                        />
                                        {validateFile.size &&
                                            <span style={{ fontSize: 12, color: "red" }}>{t("maximumFileSizeInvoice")}</span>}
                                        {validateFile.type &&
                                            <span style={{ fontSize: 12, color: "red" }}>{t("allowedFormatInvoice")}</span>}
                                        {validateFile.required &&
                                            <span style={{ fontSize: 12, color: "red" }}>{t("isRequired")}</span>}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>{t("cancel")}</Button>
                        <Button type="submit">{t("charge")}</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}
